/**
 * Slice 49 — location stamps. Two per job: one when the clean starts, one when it finishes.
 * A stamp is a real time plus the phone's location at that moment, or "location not saved".
 * Location never blocks work: takeStamp never throws.
 */

import { getStampPosition, haversineDistanceM, isGeolocationPositionError } from "@/lib/geolocation";

/** The three distances the owner can pick for "away", in metres. */
export const AWAY_DISTANCE_OPTIONS_M = [150, 300, 1000] as const;
/** Away distance for any property that has never set one. */
export const DEFAULT_AWAY_DISTANCE_M = 300;

export type StampReason = "denied" | "off" | "no_fix";

export interface JobStamp {
  /** Real time the stamp was taken on the phone (ISO). Kept unchanged through any offline wait. */
  at: string;
  lat: number | null;
  lng: number | null;
  accuracyM: number | null;
  /** Why no location was saved; null when a location was saved. */
  reason: StampReason | null;
}

export interface PropertyStampSettings {
  enabled: boolean;
  awayDistanceM: number;
  /** The property pin (properties.latitude / longitude). */
  lat: number | null;
  lng: number | null;
}

export const DEFAULT_STAMP_SETTINGS: PropertyStampSettings = {
  enabled: true,
  awayDistanceM: DEFAULT_AWAY_DISTANCE_M,
  lat: null,
  lng: null,
};

export function normalizeAwayDistanceM(value: unknown): number {
  const n = Number(value);
  return (AWAY_DISTANCE_OPTIONS_M as readonly number[]).includes(n) ? n : DEFAULT_AWAY_DISTANCE_M;
}

/** Take a stamp now. Always resolves. */
export async function takeStamp(
  settings: PropertyStampSettings,
  at: string = new Date().toISOString(),
): Promise<JobStamp> {
  if (!settings.enabled) {
    return { at, lat: null, lng: null, accuracyM: null, reason: null };
  }
  try {
    const pos = await getStampPosition();
    return {
      at,
      lat: pos.coords.latitude,
      lng: pos.coords.longitude,
      accuracyM: Number.isFinite(pos.coords.accuracy) ? pos.coords.accuracy : null,
      reason: null,
    };
  } catch (err) {
    let reason: StampReason = "no_fix";
    if (isGeolocationPositionError(err) && err.code === 1) reason = "denied";
    else if (err instanceof Error && err.message === "Geolocation not supported") reason = "off";
    return { at, lat: null, lng: null, accuracyM: null, reason };
  }
}

export function hasStampLocation(stamp: Pick<JobStamp, "lat" | "lng">): boolean {
  return stamp.lat != null && stamp.lng != null;
}

/** Distance in metres from the property pin, or null when either side has no location. */
export function stampDistanceM(
  stamp: Pick<JobStamp, "lat" | "lng">,
  settings: Pick<PropertyStampSettings, "lat" | "lng">,
): number | null {
  if (!hasStampLocation(stamp) || settings.lat == null || settings.lng == null) return null;
  return haversineDistanceM(stamp.lat!, stamp.lng!, settings.lat, settings.lng);
}

export function isAway(distanceM: number | null, awayDistanceM: number): boolean | null {
  return distanceM == null ? null : distanceM > awayDistanceM;
}

/** Columns written to cleaning_tasks for one stamp (start_location_* or finish_location_*). */
export function stampColumns(
  prefix: "start" | "finish",
  stamp: JobStamp,
  settings: PropertyStampSettings,
): Record<string, unknown> {
  if (!settings.enabled) return {};
  const distanceM = stampDistanceM(stamp, settings);
  const away = isAway(distanceM, settings.awayDistanceM);
  return {
    [`${prefix}_location_lat`]: stamp.lat,
    [`${prefix}_location_lng`]: stamp.lng,
    [`${prefix}_location_accuracy_m`]: stamp.accuracyM,
    [`${prefix}_location_distance_m`]: distanceM,
    [`${prefix}_location_verified`]: away == null ? null : !away,
    [`${prefix}_location_override_reason`]: stamp.reason,
    [`${prefix}_location_captured_at`]: stamp.at,
  };
}

/** "2 hours 37 minutes", "1 hour", "12 minutes". */
export function formatDurationWords(totalMinutes: number): string {
  const mins = Math.max(0, Math.round(totalMinutes));
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  const parts: string[] = [];
  if (h > 0) parts.push(`${h} ${h === 1 ? "hour" : "hours"}`);
  if (m > 0 || h === 0) parts.push(`${m} ${m === 1 ? "minute" : "minutes"}`);
  return parts.join(" ");
}

/** Short running label for the job header: "1 hr 12 min", "1 min". */
export function formatRunningShort(totalMinutes: number): string {
  const mins = Math.max(0, Math.floor(totalMinutes));
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h > 0) return `${h} hr ${m} min`;
  return `${m} min`;
}

export function minutesBetween(startIso: string, endIso: string): number | null {
  const a = new Date(startIso).getTime();
  const b = new Date(endIso).getTime();
  if (!Number.isFinite(a) || !Number.isFinite(b) || b < a) return null;
  return (b - a) / 60000;
}

export function formatClockTime(iso: string): string {
  return new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}

/** "35 m" under a kilometre, "2.4 km" beyond. */
export function formatStampDistance(meters: number): string {
  if (meters < 1000) return `${Math.max(0, Math.round(meters))} m`;
  return `${(meters / 1000).toFixed(1)} km`;
}

/** Plain-words distance line for the owner: "At the property · 20 m" or "2.4 km from the property". */
export function stampDistanceWords(distanceM: number | null, away: boolean | null): string | null {
  if (distanceM == null) return null;
  const d = formatStampDistance(distanceM);
  return away ? `${d} from the property` : `At the property · ${d}`;
}

export function stampMapUrl(lat: number, lng: number): string {
  return `https://www.google.com/maps?q=${lat},${lng}`;
}

/** Why a stamp has no location. Older jobs held typed text here, which is not shown. */
export function stampReasonWords(reason: string | null | undefined): string | null {
  if (reason === "denied") return "Location was not allowed";
  if (reason === "off" || reason === "no_fix") return "Phone location was off";
  return null;
}
